import {
  OpeningDetailResponse,
  SourcingOpeningUpdateParameters,
} from 'api-clients/monolith';

import { JobDetailFormState } from '../types';
import { HIRING_ADDRESS_KEYS } from './hiring-address';

/** The opening as Hire last returned it, the baseline a save compares against. */
export type SavedOpeningDetails = Pick<
  OpeningDetailResponse,
  'job_description' | 'state_code' | 'street_address' | 'city' | 'postal_code'
>;

const ADDRESS_FIELDS = [
  { field: 'streetAddress', param: 'street_address', saved: 'street_address' },
  { field: 'city', param: 'city', saved: 'city' },
  { field: 'postalCode', param: 'postal_code', saved: 'postal_code' },
] as const satisfies ReadonlyArray<{
  field: (typeof HIRING_ADDRESS_KEYS)[number];
  param: keyof SourcingOpeningUpdateParameters;
  saved: keyof SavedOpeningDetails;
}>;

/** Blank, null and undefined are one value: Hire stores a cleared field as null. */
const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a?.trim() || null) === (b?.trim() || null);

/**
 * The fields a Review Job save sends. The State and, for a remote opening,
 * the hiring address live on the Location the opening shares with its
 * siblings, so each travels only when the recruiter changed it from `saved`:
 * a value another card saved must not travel back stale from this one. An
 * on-site opening's address is edited in Hire. With no `saved` baseline every
 * field is sent. An address field cleared since the baseline travels as an
 * empty string: Hire stores it as null, and an omitted key would keep the old
 * value.
 */
export function buildOpeningUpdateBody(
  detail: JobDetailFormState,
  saved?: SavedOpeningDetails,
): SourcingOpeningUpdateParameters {
  const body: SourcingOpeningUpdateParameters = {
    description: detail.description,
    state_code:
      saved && same(detail.stateCode, saved.state_code)
        ? undefined
        : detail.stateCode,
  };
  if (detail.remote === true) {
    for (const { field, param, saved: savedKey } of ADDRESS_FIELDS) {
      if (saved && same(detail[field], saved[savedKey])) continue;
      body[param] = detail[field] ?? (saved ? '' : undefined);
    }
  }
  return body;
}

/** True when a save would change what Hire holds for the opening. */
export function hasUnsavedDetails(
  detail: JobDetailFormState,
  saved: SavedOpeningDetails,
): boolean {
  if (!same(detail.description, saved.job_description)) return true;
  if (!same(detail.stateCode, saved.state_code)) return true;
  return (
    detail.remote === true &&
    ADDRESS_FIELDS.some(
      ({ field, saved: savedKey }) => !same(detail[field], saved[savedKey]),
    )
  );
}
